<template>
  <div class="d-flex flex-column">
    <InvoiceHeader :invoice="job.invoice" @refresh-invoice="refreshInvoice" />
    <div v-if="job.invoice">
      <Invoice
        class="ssui-invoice"
        :invoice="job.invoice"
        :customizations="customizations"
        :equipment-types-enabled="equipmentTypesEnabled"
        :tenant-timezone="tenantTimezone"
        @edit-transaction-note-click="handleEditNoteClick"
        @edit-transaction-reference-id-click="handleEditReferenceIdClick"
        @equipment-request-click="toggleModal('captureModal')"
      >
        <template #paymentSchedule>
          <div
            v-if="spbEnabled && spbPlanStale && !spbPlanLocked"
            class="spb-stale-banner"
            data-cy="spb-stale-banner"
          >
            <i class="fas fa-exclamation-triangle mr-2" />
            <div>
              <strong>{{ programName }} is out of date.</strong>
              The dollar-based {{ programName }} total no longer matches the
              {{ programName }} target. Reconfigure the {{ programName }} before
              accepting any quote on this job — approval will be blocked until
              the {{ programName }} is updated.
            </div>
          </div>
          <div
            v-if="spbEnabled && spbPlanNeedsReview && !spbPlanLocked"
            class="spb-review-banner"
            data-cy="spb-review-banner"
          >
            <i class="fas fa-info-circle mr-2" />
            <div>
              <strong>{{ programName }} needs review.</strong>
              The invoice total has changed since this {{ programName }} was
              created. Open Edit {{ programName }}, confirm the
              {{ intervalName.toLowerCase() }} percentages against the updated
              balance, and save. Invoice approval will be blocked until the
              {{ programName }} is reviewed.
            </div>
          </div>
          <PaymentScheduleSection
            v-if="jobPaymentSchedule"
            :schedule="jobPaymentSchedule"
            :payment-context="job.invoice?.progress_billing ?? null"
          />
        </template>
      </Invoice>
      <EquipmentCaptureModal
        v-if="captureModal"
        :invoice="job.invoice"
        :address="job.address"
        :equipment="customerEquipment"
        @hide-modal="toggleModal('captureModal')"
        @refresh-invoice="refreshInvoice"
      />
      <EditTransactionNotesModal
        v-if="editNotesModal"
        :transaction="selectedTransaction"
        @hide-modal="clearNotesModal"
      />
      <TransactionReferenceIdModal
        v-if="editReferenceIdModal"
        :transaction="selectedTransaction"
        @hide-modal="clearReferenceIdModal"
      />
    </div>
    <div v-else class="no-invoice">
      <i class="fad fa-question-circle"></i>
      No Invoice Available
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { EventBus } from "@/lib/event_bus.js";

import EquipmentCaptureModal from "@/Equipment/EquipmentCaptureModal.vue";
import InvoiceHeader from "@/Invoice/InvoiceHeader.vue";
import EditTransactionNotesModal from "@/Job/EditTransactionNotesModal.vue";
import TransactionReferenceIdModal from "@/Job/TransactionReferenceIdModal.vue";
import PaymentScheduleSection from "@/serviceshift-ui/components/QuoteInvoice/PaymentScheduleSection.vue";

export default {
  components: {
    TransactionReferenceIdModal,
    InvoiceHeader,
    EditTransactionNotesModal,
    EquipmentCaptureModal,
    PaymentScheduleSection
  },
  data() {
    return {
      editNotesModal: false,
      editReferenceIdModal: false,
      selectedTransaction: null,
      captureModal: false,
      jobPaymentSchedule: null,
      jobPaymentInvoice: null,
      jobPaymentScheduleLoading: false,
      progressBillingConfig: null
    };
  },
  computed: {
    ...mapGetters(["featureEnabledForTenant"]),
    ...mapState({
      job: "job",
      customerEquipment: "customerEquipment",
      customizations: ({ tenantCustomizations, tenant }) => {
        if (!tenant || !tenantCustomizations) {
          return null;
        }
        return {
          ...tenantCustomizations,
          Tenant: { ...tenant }
        };
      },
      equipmentTypesEnabled({ tenant }) {
        if (!tenant) return false;
        return tenant.feature_flags.equipment_types;
      },
      tenantTimezone({ tenant }) {
        if (!tenant) return "";
        return tenant.timezone;
      }
    }),
    progressBillingEnabled() {
      return (
        this.featureEnabledForTenant("progress_billing") &&
        Boolean(this.progressBillingConfig?.enabled)
      );
    },
    existingProgressBillingInvoice() {
      return this.job?.invoice?.progress_billing?.is_progress_billing === true;
    },
    shouldLoadProgressBillingSchedule() {
      return (
        this.featureEnabledForTenant("progress_billing") &&
        (this.progressBillingEnabled || this.existingProgressBillingInvoice)
      );
    },
    programName() {
      return (
        this.$store.state.tenant?.feature_flags
          ?.progress_billing_program_name || "Payment Schedule"
      );
    },
    intervalName() {
      return (
        this.$store.state.tenant?.feature_flags
          ?.progress_billing_interval_name || "Installment"
      );
    },
    spbEnabled() {
      return Boolean(this.jobPaymentSchedule);
    },
    spbPlanLocked() {
      return Boolean(this.job?.invoice?.finalized);
    },
    spbPlanDrift() {
      return Boolean(this.job?.invoice?.progress_billing?.plan_drift);
    },
    spbPlanNeedsReview() {
      return Boolean(this.job?.invoice?.progress_billing?.plan_needs_review);
    },
    spbAllocationDrift() {
      const plan = this.jobPaymentSchedule;
      if (!plan || plan.split_mode !== "amount") return false;
      const planTotal = (plan.installments || []).reduce(
        (sum, i) => sum + Number(i.value || 0),
        0
      );
      const target = plan.target_total_cents ?? 0;
      return target > 0 && planTotal !== target;
    },
    spbPlanStale() {
      return this.spbPlanDrift || this.spbAllocationDrift;
    }
  },
  watch: {
    "job.id": {
      immediate: false,
      handler(newId) {
        if (newId) this.loadJobPaymentSchedule();
      }
    }
  },
  async created() {
    EventBus.$on("refresh-invoice", this.refreshInvoice);
    await this.loadProgressBillingConfig();
    await this.loadJobPaymentSchedule();
  },
  beforeUnmount() {
    EventBus.$off("refresh-invoice", this.refreshInvoice);
  },
  methods: {
    ...mapActions([
      "fetchJob",
      "fetchJobPaymentSchedule",
      "fetchProgressBillingConfig"
    ]),
    toggleModal(modal) {
      this[modal] = !this[modal];
    },
    handleEditNoteClick(transaction) {
      this.selectedTransaction = transaction;
      this.editNotesModal = true;
    },
    handleEditReferenceIdClick(transaction) {
      this.selectedTransaction = transaction;
      this.editReferenceIdModal = true;
    },
    clearNotesModal() {
      this.editNotesModal = false;
      this.selectedTransaction = null;
    },
    clearReferenceIdModal() {
      this.editReferenceIdModal = false;
      this.selectedTransaction = null;
    },
    refreshInvoice() {
      this.fetchJob({ jobId: this.job.id });
      // Local `jobPaymentSchedule` is the source of truth for the
      // Payment Schedule table and Edit modal — reload it any time the
      // invoice gets refreshed so a refund-with-cancel or a deposit
      // payment is reflected immediately, without forcing a page reload.
      if (this.shouldLoadProgressBillingSchedule) {
        this.loadJobPaymentSchedule();
      }
    },

    async loadProgressBillingConfig() {
      if (!this.featureEnabledForTenant("progress_billing")) return;
      try {
        this.progressBillingConfig = await this.fetchProgressBillingConfig();
      } catch {
        this.progressBillingConfig = null;
      }
    },

    async loadJobPaymentSchedule() {
      if (!this.shouldLoadProgressBillingSchedule) return;
      if (!this.job?.id) return;
      this.jobPaymentScheduleLoading = true;
      try {
        const response = await this.fetchJobPaymentSchedule(this.job.id);
        this.applyScheduleResponse(response);
      } finally {
        this.jobPaymentScheduleLoading = false;
      }
    },

    applyScheduleResponse(response) {
      if (!response) {
        this.jobPaymentSchedule = null;
        this.jobPaymentInvoice = null;
        return;
      }
      this.jobPaymentSchedule = response.schedule ?? null;
      this.jobPaymentInvoice = response.invoice ?? null;
    }
  }
};
</script>

<style lang="scss" scoped>
.no-invoice {
  color: var(--gray);
  font-size: 1.1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 15px;
  i {
    font-size: 4rem;
    margin-bottom: 10px;
  }
}

:deep(.invoice-body) {
  .table {
    margin-bottom: 0;
    border-radius: 4px 4px 0 0;
  }
  .totals {
    box-shadow: var(--box-shadow);
  }
}

:deep(.table td) {
  border-top: none;
  vertical-align: baseline !important;
}

.financing-option {
  display: flex;
  align-items: center;
  padding-left: 12px;
  border-top: 1px solid;
  background: #f4f5f8;
}

.spb-stale-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0.5rem 0 0.75rem;
  padding: 0.75rem 1rem;
  border-left: 4px solid #f59e0b;
  background-color: #fffbeb;
  color: #78350f;
  font-size: 0.9rem;
  line-height: 1.4;

  i {
    margin-top: 2px;
    color: #b45309;
  }

  strong {
    display: block;
    margin-bottom: 0.15rem;
  }
}

.spb-review-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 1.5rem 0 0.75rem;
  padding: 0.75rem 1rem;
  border-left: 4px solid #3b82f6;
  background-color: #eff6ff;
  color: #1e3a8a;
  font-size: 0.9rem;
  line-height: 1.4;

  i {
    margin-top: 2px;
    color: #1d4ed8;
  }

  strong {
    display: block;
    margin-bottom: 0.15rem;
  }
}

.spb-row__action {
  padding: 0;
  background: transparent;
  border: 0;
  color: var(--primary-color);
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }
}
</style>
